import api from "@services/api";

/** Where to get the weather: coordinates, or a town for the API to look up. */
export type WeatherPlace =
  { lat: number; lng: number } | { town: string; country?: string };

/** The API's weather at a place: whole °C, km/h, OpenWeather's condition text and icon code. */
export type WeatherReport = {
  /** The place's clock, in seconds east of UTC, e.g. 36000 for Brisbane. */
  utcOffsetSeconds: number;
  current: {
    temp: number;
    humidity: number;
    windKmh: number;
    description: string;
    /** OpenWeather's icon code, e.g. "01n"; a trailing "n" means night. */
    icon: string;
    observedAt: string;
  };
  /** The rest of today, from now. */
  today: { high: number; low: number };
  /** The whole days after today the forecast covers, 4 or 5; `date` is the place's own calendar date, e.g. "2026-10-09". */
  daily: {
    date: string;
    icon: string;
    description: string;
    high: number;
    low: number;
  }[];
};

/**
 * The ~1 km cell the API shares one report across: two decimal degrees, a half rounding up, so -27.475 is in -27.47's.
 * Must round as the API's `toCell` (api/app/Services/OpenWeatherService.ts) does, or the location menu lists one
 * report twice, or hides one.
 */
export const toWeatherCell = (degrees: number) =>
  Math.round(degrees * 100) / 100;

export const getWeather = async (
  params: WeatherPlace & { lang: string },
): Promise<WeatherReport> => {
  const { data } = await api.get("/weather", { params });
  return data;
};
